import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SignatureCapture } from '@/components/SignatureCapture';
import { PhotoCapture } from '@/components/PhotoCapture';
import { BillPhotos } from '@/components/BillPhotos';
import { AvsToggles } from '@/components/AvsToggles';
import { ShipmentInfoCard } from '@/components/ShipmentInfoCard';
import { NavigateButton } from '@/components/NavigateButton';
import { RouteMapPreview } from '@/components/RouteMapPreview';
import { GpsStatusLine } from '@/components/GpsStatusLine';
import { useDeliveryStore, deliverySelectors } from '@/stores/deliveryStore';
import { useWorkflowBack } from '@/lib/useWorkflowBack';
import { OsdOptions, type OsdValue } from '@/types/domain';

export function DeliveryProgressPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stop = useDeliveryStore((s) => s.stop);
  const phase = useDeliveryStore((s) => s.phase);
  const loadStop = useDeliveryStore((s) => s.loadStop);
  const reset = useDeliveryStore((s) => s.reset);
  const stepBack = useDeliveryStore((s) => s.stepBack);

  useEffect(() => {
    if (id && !stop) void loadStop(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (phase === 'idle' && stop && id) {
      navigate(`/unload/${id}`, { replace: true });
    }
  }, [phase, stop, id, navigate]);

  useWorkflowBack(
    phase !== 'idle' && phase !== 'complete' && phase !== 'terminalReturn' && phase !== 'atTerminal',
    async () => {
      await stepBack();
      if (useDeliveryStore.getState().phase === 'idle' && id) {
        navigate(`/unload/${id}`, { replace: true });
      }
    },
  );

  useEffect(() => {
    if (phase !== 'complete') return;
    const t = setTimeout(() => {
      reset();
      navigate('/main', { replace: true });
    }, 2000);
    return () => clearTimeout(t);
  }, [phase, navigate, reset]);

  if (!stop) {
    return (
      <AppShell title="Delivery" showBack>
        <p className="text-brand-muted">Loading...</p>
      </AppShell>
    );
  }

  return (
    <AppShell title={`Delivery: ${stop.name || stop.address}`}>
      {phase === 'enRoute' && <EnRoutePhase />}
      {phase === 'terminalReturn' && <TerminalReturnPhase />}
      {phase === 'atTerminal' && <AtTerminalPhase />}
      {phase === 'arrived' && <ArrivedPhase />}
      {phase === 'workBills' && <WorkBillsPhase />}
      {phase === 'signatureGate' && <SignaturePhase />}
      {phase === 'complete' && (
        <Card>
          <CardBody>
            <p className="font-semibold text-brand-delivery">Delivery complete.</p>
            <p className="text-sm text-brand-muted">Returning to the main menu...</p>
            <Button
              variant="primary"
              onClick={() => {
                reset();
                navigate('/main', { replace: true });
              }}
            >
              Back to main menu
            </Button>
          </CardBody>
        </Card>
      )}
    </AppShell>
  );
}

function EnRoutePhase() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stop = useDeliveryStore((s) => s.stop);
  const arrive = useDeliveryStore((s) => s.arrive);
  const cancelEnRoute = useDeliveryStore((s) => s.cancelEnRoute);
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      {stop && <ShipmentInfoCard stop={stop} />}
      <Card>
      <CardHeader>
        <CardTitle>En route to delivery</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <GpsStatusLine />
        {stop && (
          <RouteMapPreview
            destination={{ name: stop.name, address: stop.address, city: stop.city }}
          />
        )}
        {stop && (
          <NavigateButton
            accent="delivery"
            destination={{ name: stop.name, address: stop.address, city: stop.city }}
          />
        )}
        <Button
          variant="delivery"
          size="xl"
          fullWidth
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await arrive();
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Recording...' : 'ARRIVED'}
        </Button>
        <Button
          variant="outline"
          size="lg"
          fullWidth
          onClick={async () => {
            await cancelEnRoute();
            navigate(`/unload/${id}`, { replace: true });
          }}
        >
          CANCEL
        </Button>
      </CardBody>
    </Card>
    </div>
  );
}

// Dock delivery: returning to terminal, then a prominent red ARRIVED AT TERMINAL.
function TerminalReturnPhase() {
  const arriveAtTerminal = useDeliveryStore((s) => s.arriveAtTerminal);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Returning to terminal</CardTitle>
      </CardHeader>
      <CardBody>
        <div className="rounded-button bg-brand-error px-3 py-2 text-center text-sm font-bold text-white">
          RETURNING TO TERMINAL
        </div>
        <Button
          size="xl"
          fullWidth
          className="bg-brand-error text-white hover:opacity-90 focus:ring-brand-error"
          onClick={() => void arriveAtTerminal()}
        >
          ARRIVED AT TERMINAL
        </Button>
      </CardBody>
    </Card>
  );
}

function AtTerminalPhase() {
  const unloadAtDock = useDeliveryStore((s) => s.unloadAtDock);
  return (
    <Card>
      <CardHeader>
        <CardTitle>At terminal</CardTitle>
      </CardHeader>
      <CardBody>
        <Button variant="delivery" size="xl" fullWidth onClick={() => void unloadAtDock()}>
          UNLOAD AT DOCK
        </Button>
      </CardBody>
    </Card>
  );
}

function ArrivedPhase() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stop = useDeliveryStore((s) => s.stop);
  const begin = useDeliveryStore((s) => s.beginUnloading);
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      {stop && <ShipmentInfoCard stop={stop} />}
      <Card>
      <CardHeader>
        <CardTitle>Arrived at delivery</CardTitle>
      </CardHeader>
      <CardBody>
        <Button
          variant="delivery"
          size="xl"
          fullWidth
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await begin();
            } finally {
              setBusy(false);
            }
          }}
        >
          START UNLOADING
        </Button>
        <Button variant="danger" size="lg" fullWidth onClick={() => navigate(`/unload/${id}/dead-call`)}>
          DEAD CALL
        </Button>
      </CardBody>
    </Card>
    </div>
  );
}

function WorkBillsPhase() {
  const stop = useDeliveryStore((s) => s.stop);
  const bills = useDeliveryStore((s) => s.bills);
  const avs = useDeliveryStore((s) => s.avs);
  const setAvs = useDeliveryStore((s) => s.setAvs);
  const setNoFreight = useDeliveryStore((s) => s.setBillNoFreight);
  const setConfirmed = useDeliveryStore((s) => s.setBillConfirmed);
  const setNotes = useDeliveryStore((s) => s.setBillNotes);
  const setOsd = useDeliveryStore((s) => s.setBillOsd);
  const addPhoto = useDeliveryStore((s) => s.addBillPhoto);
  const removePhoto = useDeliveryStore((s) => s.removeBillPhoto);
  const setPodDoc = useDeliveryStore((s) => s.setBillPodDoc);
  const cancelUnloading = useDeliveryStore((s) => s.cancelUnloading);
  const canContinue = useDeliveryStore(deliverySelectors.canContinueToSignature);

  // Native auto-advances to the POD signature gate once every bill is handled.
  useEffect(() => {
    if (canContinue) useDeliveryStore.setState({ phase: 'signatureGate' });
  }, [canContinue]);

  if (!stop) return null;

  return (
    <div className="space-y-3">
      <Card>
        <CardBody>
          <AvsToggles
            values={avs}
            onChange={(patch) => setAvs(patch)}
            fields={['inside', 'tailgate', 'residential']}
          />
        </CardBody>
      </Card>
      {stop.bills.map((b) => {
        const bs = bills[b.remF04Id];
        return (
          <Card key={b.remF04Id}>
            <CardHeader>
              <CardTitle>{b.remF02Tag ?? b.remF04Id}</CardTitle>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-5 w-5"
                  checked={bs?.noFreight ?? false}
                  onChange={(e) => setNoFreight(b.remF04Id, e.target.checked)}
                />
                No Freight / Cannot deliver
              </label>
            </CardHeader>
            {b.remShipperName && (
              <p className="-mt-2 mb-3 text-base text-brand-muted">
                Picked up from:{' '}
                <span className="font-semibold text-brand-text">{b.remShipperName}</span>
              </p>
            )}
            {!bs?.noFreight && (
              <CardBody>
                <Button
                  variant={bs?.confirmed ? 'outline' : 'delivery'}
                  size="lg"
                  fullWidth
                  onClick={() => setConfirmed(b.remF04Id, !bs?.confirmed)}
                >
                  {bs?.confirmed ? 'Bill delivered ✓' : 'Confirm bill delivered'}
                </Button>
                <label className="block text-sm font-semibold text-brand-muted">OSD status</label>
                <select
                  className="w-full rounded-button bg-brand-surfaceAlt p-3 text-base"
                  value={bs?.osd ?? 'Clear'}
                  onChange={(e) => setOsd(b.remF04Id, e.target.value as OsdValue)}
                >
                  {OsdOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                <Input
                  label="Notes"
                  value={bs?.notes ?? ''}
                  onChange={(e) => setNotes(b.remF04Id, e.target.value)}
                />
                <BillPhotos
                  photos={bs?.photos ?? []}
                  onAdd={(blob) => addPhoto(b.remF04Id, blob)}
                  onRemove={(i) => removePhoto(b.remF04Id, i)}
                />
                <div className="mt-2">
                  <label className="block text-sm font-semibold text-brand-muted">
                    POD document {bs?.podDoc ? '(captured)' : '(optional)'}
                  </label>
                  <PhotoCapture
                    label="Capture POD"
                    value={bs?.podDoc ?? null}
                    onCapture={(blob) => setPodDoc(b.remF04Id, blob)}
                    onRemove={() => setPodDoc(b.remF04Id, null)}
                  />
                </div>
              </CardBody>
            )}
          </Card>
        );
      })}
      <Button variant="danger" size="lg" fullWidth onClick={() => cancelUnloading()}>
        CANCEL UNLOADING
      </Button>
    </div>
  );
}

function SignaturePhase() {
  const captureSig = useDeliveryStore((s) => s.captureSignature);
  const setSignerName = useDeliveryStore((s) => s.setSignerName);
  const signerName = useDeliveryStore((s) => s.signerName);
  const complete = useDeliveryStore((s) => s.complete);
  // Signature is mandatory only when a delivered bill has no scanned POD doc
  // (interline deliveries run on the interline carrier's DR - the scan is the
  // legal POD, so no receiver signature is needed).
  const sigRequired = useDeliveryStore(deliverySelectors.signatureRequired);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (signing) {
    return (
      <SignatureCapture
        accent="delivery"
        onCancel={() => setSigning(false)}
        onAccept={async (blob) => {
          captureSig(blob);
          setSigned(true);
          setSigning(false);
        }}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>POD signature</CardTitle>
      </CardHeader>
      <CardBody>
        <Button variant="outline" size="lg" fullWidth onClick={() => setSigning(true)}>
          {signed ? 'Signature Captured \u2713' : sigRequired ? 'Tap to Sign' : 'Tap to Sign (optional)'}
        </Button>
        {!sigRequired && (
          <p className="text-xs text-brand-muted">
            All POD documents scanned. The scanned documents serve as proof of delivery, so a
            signature is optional.
          </p>
        )}
        <Input label="Receiver name" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
        <Button
          variant="delivery"
          size="xl"
          fullWidth
          disabled={submitting || (sigRequired && !signed) || (signed && !signerName.trim())}
          onClick={async () => {
            setSubmitting(true);
            try {
              await complete();
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting ? 'Submitting...' : 'DELIVERED'}
        </Button>
      </CardBody>
    </Card>
  );
}
